import React from "react";
import { Container, Stack, Typography, useTheme } from "@mui/material";
import { keyframes, styled } from "@mui/system";
import { getRandomMessage } from "./LoadingMessages";

// Fade animation for petals
function FruitifySpinner({ size = 150 }) {
  const theme = useTheme();
  const rotationSeconds = 1.5;
  const fadeCircular = keyframes`
      0% { opacity: 0.2; }
      10% { opacity: 0.4; }
      20% { opacity: 0.6; }
      30% { opacity: 0.8; }
      40% { opacity: 1; }
      50% { opacity: 0.8; }
      60% { opacity: 0.6; }
      70% { opacity: 0.4; }
      80% { opacity: 0.2; }
      100% { opacity: 0.2; }
    `;

  const AnimatedPetal = styled("path")(({ delay }) => ({
    animation: `${fadeCircular} ${rotationSeconds}s linear infinite`,
    animationDelay: delay,
    animationFillMode: "both", // ensures initial opacity is correct
  }));

  const petals = [
    {
      d: "M240.03,98.44c1.53-12.94,5.67-32.67,17.88-50.14l.03-.03s-.02-.05-.02-.11v-.03c-.02-.07,0-.21,0-.39,0-.09,0-.18.02-.29l-26.56,26.56c.86,2.06.48,4.51-1.2,6.2-2.24,2.24-5.87,2.23-8.1,0l-.06-.06-.03-.03-.03-.03c-2.23-2.23-2.24-5.85,0-8.1,1.69-1.69,4.14-2.06,6.2-1.2l26.53-26.53.03-.03c-.2.01-.39.03-.54.03-.14-.02-.26-.01-.27-.03-.03.03-.06.03-.09.06-17.41,12.14-37.08,16.31-49.99,17.82-14.04,11.51-25.3,30.47-25.3,61.57,31.05-.01,49.98-11.21,61.51-25.24Z",
      fill: theme.palette.secondary.main,
    },
    {
      d: "M302.19,151.09s-53.89-58.43-112.31,0c58.43,58.43,112.31,0,112.31,0Z",
      fill: theme.palette.primary.main,
    },
    {
      d: "M257.93,257.93s3.21-79.42-79.42-79.42c0,82.63,79.42,79.42,79.42,79.42Z",
      fill: theme.palette.primary.main,
    },
    {
      d: "M151.09,189.87s-58.43,53.89,0,112.31c58.43-58.43,0-112.31,0-112.31Z",
      fill: theme.palette.primary.main,
    },
    {
      d: "M123.67,178.51s-79.42-3.21-79.42,79.42c82.63,0,79.42-79.42,79.42-79.42Z",
      fill: theme.palette.primary.main,
    },
    {
      d: "M112.31,151.09s-53.89-58.43-112.31,0c58.43,58.43,112.31,0,112.31,0Z",
      fill: theme.palette.primary.main,
    },
    {
      d: "M123.67,123.67s3.21-79.42-79.42-79.42c0,82.63,79.42,79.42,79.42,79.42Z",
      fill: theme.palette.primary.main,
    },
    {
      d: "M151.09,0s-58.43,53.89,0,112.31c58.43-58.43,0-112.31,0-112.31Z",
      fill: theme.palette.primary.main,
    },
  ];
  const total = petals.length;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 302.19 302.19"
      data-testid="fruitify-spinner"
    >
      {petals.map((petal, i) => {
        // stagger delay based on petal index
        const delay = `${(i * rotationSeconds) / total}s`;
        return (
          <AnimatedPetal key={i} d={petal.d} fill={petal.fill} delay={delay} />
        );
      })}
    </svg>
  );
}

function FruitifyLoading({
  category = null,
  size = 150,
  direction = "column",
}) {
  if (!category) {
    throw Error("category is required");
  }
  return (
    <GeneralLoading
      size={size}
      direction={direction}
      message={getRandomMessage(category)}
    />
  );
}

export default function GeneralLoading({
  message = "Loading...",
  size = 150,
  direction = "column",
}) {
  if (direction === "column") {
    return (
      <Container data-testid="loading-spinner">
        <Stack
          direction={direction}
          spacing={2}
          sx={{ alignItems: "center", justifyContent: "center" }}
        >
          <Typography variant="body1" component="div">
            {message}
          </Typography>
          <FruitifySpinner size={size} aria-label="Loading Spinner" />
        </Stack>
      </Container>
    );
  } else if (direction === "row") {
    return (
      <Stack
        direction={direction}
        spacing={2}
        sx={{ alignItems: "center" }}
        data-testid="loading-spinner"
      >
        <FruitifySpinner size={size} aria-label="Loading Spinner" />

        <Typography variant="body1" component="div">
          {message}
        </Typography>
      </Stack>
    );
  } else {
    throw Error("Unknown direction, should be row or column");
  }
}

export { FruitifyLoading };
